import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { ContainerService } from '../../../../../shared/services/api/c4-model/ContainerService';
import { C4ModelUsuariosService } from '../../../../../shared/services/api/c4-model/UsuariosService';

type Props = {
  open: boolean;
  onClose: () => void;
  onSave?: (values: any) => void;
  initialValues?: {
    id?: string;
    usuarioId?: string;
    containerId?: string;
    acao?: string;
    meioAcesso?: string;
  };
};

export const ModalUsuarios: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  initialValues,
}) => {
  const [containers, setContainers] = useState<{ id: string; nome: string }[]>(
    []
  );
  const [usuarios, setUsuarios] = useState<{ id: string; nome: string }[]>([]);
  const [usuarioId, setUsuarioId] = useState('');
  const [containerId, setContainerId] = useState('');
  const [acao, setAcao] = useState('');
  const [meioAcesso, setMeioAcesso] = useState('');
  const [isValid, setIsValid] = useState(false);

  // Mapeamento do valor do enum para o ID da API
  const mapEnumToApiId = (enumValue: string): string => {
    const mapping: { [key: string]: string } = {
      PARCEL: '0',
      FRONTEND: '1',
      BACKEND: '2',
      BIBLIOTECA: '3',
      FULLSTACK: '4',
      WEBCOMPONENT: '5',
      ORQUESTRADOR: '6',
    };
    return mapping[enumValue] || enumValue;
  };

  // Mapeamento do ID da API para o valor do enum
  const mapApiIdToEnum = (apiId: string): string => {
    const mapping: { [key: string]: string } = {
      '0': 'PARCEL',
      '1': 'FRONTEND',
      '2': 'BACKEND',
      '3': 'BIBLIOTECA',
      '4': 'FULLSTACK',
      '5': 'WEBCOMPONENT',
      '6': 'ORQUESTRADOR',
    };
    return mapping[apiId] || apiId;
  };

  useEffect(() => {
    ContainerService.getAll().then((data) => {
      setContainers((data as any) || []);
    });

    C4ModelUsuariosService.getAll('1', 100, 0).then((data) => {
      if (data && !(data instanceof Error)) {
        setUsuarios(
          data.data.map((user) => ({ id: user.id as string, nome: user.nome }))
        );
      }
    });
  }, []);

  useEffect(() => {
    if (open) {
      if (initialValues) {
        setUsuarioId(initialValues.usuarioId || '');
        // Converter o valor do enum para o ID da API
        const mappedContainerId = mapEnumToApiId(
          initialValues.containerId || ''
        );
        setContainerId(mappedContainerId);
        setAcao(initialValues.acao || '');
        setMeioAcesso(initialValues.meioAcesso || '');
      } else {
        setUsuarioId('');
        setContainerId('');
        setAcao('');
        setMeioAcesso('');
      }
    }
  }, [open, initialValues]);

  useEffect(() => {
    const allFilled = Boolean(
      usuarioId.trim() &&
        String(containerId).trim() &&
        acao.trim() &&
        meioAcesso.trim()
    );
    setIsValid(allFilled);
  }, [usuarioId, containerId, acao, meioAcesso]);

  const handleSave = () => {
    // Converter o ID da API de volta para o valor do enum
    const enumValue = mapApiIdToEnum(containerId);
    const values: any = { usuarioId, containerId: enumValue, acao, meioAcesso };
    if (initialValues && initialValues.id) values.id = initialValues.id;
    if (onSave) onSave(values);
    onClose();
  };

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {initialValues?.id ? 'Editar Usuário' : 'Adicionar Usuário'}
      </DialogTitle>
      <DialogContent dividers>
        <FormControl fullWidth margin="normal" error={false}>
          <InputLabel id="usuario-label">Usuário</InputLabel>
          <Select
            labelId="usuario-label"
            id="usuario-select"
            value={usuarioId}
            label="Usuário"
            onChange={(e) => setUsuarioId(String(e.target.value))}
          >
            {usuarios.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal" error={false}>
          <InputLabel id="container-label">Container</InputLabel>
          <Select
            labelId="container-label"
            id="container-select"
            value={containerId}
            label="Container"
            onChange={(e) => setContainerId(String(e.target.value))}
          >
            {containers.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Ação"
          margin="normal"
          value={acao}
          onChange={(e) => setAcao(e.target.value)}
          error={false}
        />

        <TextField
          fullWidth
          label="Meio de acesso"
          margin="normal"
          value={meioAcesso}
          onChange={(e) => setMeioAcesso(e.target.value)}
          error={false}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          color="primary"
          disabled={!isValid}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
